import { computed, toValue, watchEffect } from "vue";
import type { ComputedRef, Ref } from "vue";

type SeoBreadcrumb = {
  name: string;
  item: string;
};

type SeoFaq = {
  question: string;
  answer: string;
};

type SeoValue<T> = T | Ref<T> | ComputedRef<T> | (() => T);

type UsePageSeoOptions = {
  title: SeoValue<string>;
  description: SeoValue<string>;
  keywords?: SeoValue<string | undefined>;
  breadcrumbs?: SeoValue<SeoBreadcrumb[]>;
  faqs?: SeoValue<SeoFaq[]>;
  robots?: SeoValue<string>;
  schemaType?: SeoValue<string>;
  schemaProperties?: SeoValue<Record<string, unknown> | undefined>;
  showBreadcrumbs?: SeoValue<boolean>;
};

const SITE_URL = "https://ustaad.today";

const ensureTrailingSlash = (path: string) => (path.endsWith("/") ? path : `${path}/`);

const asAbsoluteUrl = (value: string) => {
  if (!value) {
    return SITE_URL;
  }

  if (value.startsWith("http://") || value.startsWith("https://")) {
    return value;
  }

  if (!value.startsWith("/")) {
    return `${SITE_URL}/${value}`;
  }

  return `${SITE_URL}${value}`;
};

export const usePageSeo = (options: UsePageSeoOptions) => {
  const route = useRoute();
  const { locale } = useI18n();
  const breadcrumbState = useGlobalBreadcrumbs();

  const canonicalPath = computed(() => ensureTrailingSlash(route.path || "/"));
  const canonicalUrl = computed(() => asAbsoluteUrl(canonicalPath.value));

  watchEffect(() => {
    const breadcrumbs = toValue(options.breadcrumbs) || [];
    const showBreadcrumbs = toValue(options.showBreadcrumbs) ?? !route.meta?.hideGlobalBreadcrumbs;

    breadcrumbState.value = {
      path: route.path,
      items: breadcrumbs.map((entry) => ({
        label: entry.name,
        to: ensureTrailingSlash(entry.item),
      })),
      hidden: !showBreadcrumbs || breadcrumbs.length <= 1,
    };
  });

  useHead(() => {
    const title = toValue(options.title);
    const description = toValue(options.description);
    const keywords = toValue(options.keywords);
    const robots = toValue(options.robots) || "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1";
    const breadcrumbs = toValue(options.breadcrumbs) || [];
    const faqs = toValue(options.faqs) || [];
    const schemaType = toValue(options.schemaType) || "WebPage";
    const schemaProperties = toValue(options.schemaProperties) || {};

    const webPageSchema = {
      "@context": "https://schema.org",
      "@type": schemaType,
      name: title,
      description,
      url: canonicalUrl.value,
      inLanguage: locale.value === "rn" ? "ur-PK" : locale.value,
      isPartOf: {
        "@type": "WebSite",
        name: "Ustaad",
        url: SITE_URL,
      },
      ...schemaProperties,
    };

    const script = [
      {
        type: "application/ld+json",
        children: JSON.stringify(webPageSchema),
      },
    ];

    if (breadcrumbs.length) {
      script.push({
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: breadcrumbs.map((entry, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: entry.name,
            item: asAbsoluteUrl(entry.item),
          })),
        }),
      });
    }

    if (faqs.length) {
      script.push({
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((faq) => ({
            "@type": "Question",
            name: faq.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: faq.answer,
            },
          })),
        }),
      });
    }

    const meta = [
      { name: "description", content: description },
      { name: "robots", content: robots },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Ustaad" },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:url", content: canonicalUrl.value },
      { property: "og:locale", content: locale.value === "rn" ? "ur_PK" : locale.value.replace("-", "_") },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
    ];

    if (keywords) {
      meta.unshift({ name: "keywords", content: keywords });
    }

    return {
      title,
      meta,
      link: [
        { rel: "canonical", href: canonicalUrl.value },
        { rel: "alternate", hreflang: "ur-PK", href: canonicalUrl.value },
        { rel: "alternate", hreflang: "x-default", href: canonicalUrl.value },
      ],
      script,
    };
  });
};
